import type { SmartCreationStructure } from "@adpilot/shared";
import { useTranslation } from "react-i18next";

const lineStyle = { fill: "none", stroke: "var(--paper-line)", strokeWidth: 1.5 };
function CampaignShape({ x, automatic }: { x: number; automatic: boolean }) {
  return automatic ? (
    <circle
      cx={x}
      cy={84}
      r={10}
      fill="var(--paper-surface)"
      stroke="var(--paper-ink)"
      strokeWidth={1.4}
    />
  ) : (
    <path
      d={`M${x} 74l10 10-10 10-10-10Z`}
      fill="var(--paper-surface)"
      stroke="var(--paper-ink)"
      strokeWidth={1.4}
    />
  );
}
function GroupShape({ x }: { x: number }) {
  return (
    <rect
      x={x - 8}
      y={126}
      width={16}
      height={16}
      rx={2}
      fill="var(--paper-surface)"
      stroke="var(--paper-ink)"
    />
  );
}
function TargetShape({ x, kind }: { x: number; kind: "A" | "K" | "P" }) {
  return (
    <g>
      <rect x={x - 11} y={179} width={22} height={22} rx={5} fill="var(--paper-subtle)" />
      <text x={x} y={194} textAnchor="middle" fontSize={10} fill="var(--paper-ink)">
        {kind}
      </text>
    </g>
  );
}
/** These examples explain ownership; exact executable counts come from the evaluation below. */
export function StructureConceptDiagram({ structure }: { structure: SmartCreationStructure }) {
  const { t } = useTranslation("smartCreation");
  const binary = structure === "binary";
  const precise = structure === "precise";
  const positions = binary ? [66, 226] : [56, 172, 288];
  return (
    <figure
      data-testid={`structure-diagram-${structure}`}
      data-structure-concept={structure}
      className="w-full px-3 py-2.5"
    >
      <figcaption className="sr-only">
        {t("structure.topology.diagram")} · {t(`structure.${structure}Description`)}
      </figcaption>
      <svg
        viewBox="0 0 344 224"
        className="h-56 w-full"
        aria-hidden="true"
        style={{ fontFamily: "var(--font-sans)" }}
      >
        <rect x={116} y={10} width={112} height={28} rx={7} fill="var(--paper-ink)" />
        <text x={172} y={28} textAnchor="middle" fontSize={11} fill="var(--paper-surface)">
          {t("structure.concept.productGroup")}
        </text>
        <path d={`M172 38v18M${positions[0]} 56H${positions.at(-1)}`} {...lineStyle} />
        {positions.map((x, index) => (
          <g key={x}>
            <path d={`M${x} 56v18`} {...lineStyle} />
            <CampaignShape x={x} automatic={index === 0} />
            {binary && index === 1 ? (
              <>
                <path d="M226 94v16M150 110H302" {...lineStyle} />
                {[150, 226, 302].map((groupX, groupIndex) => (
                  <g key={groupX}>
                    <path d={`M${groupX} 110v16M${groupX} 142v36`} {...lineStyle} />
                    <GroupShape x={groupX} />
                    <TargetShape x={groupX} kind={groupIndex === 2 ? "P" : "K"} />
                  </g>
                ))}
                <text x={x} y={218} textAnchor="middle" fontSize={10} fill="var(--paper-ink)">
                  {t("structure.concept.manual")}
                </text>
              </>
            ) : (
              <>
                <path d={`M${x} 94v32`} {...lineStyle} />
                <GroupShape x={x} />
                {!precise && !binary && index > 0 ? (
                  <>
                    <path
                      d={`M${x} 142v20M${x - (index === 1 ? 32 : 16)} 162H${x + (index === 1 ? 32 : 16)}`}
                      {...lineStyle}
                    />
                    {(index === 1 ? [x - 32, x, x + 32] : [x - 16, x + 16]).map((targetX) => (
                      <g key={targetX}>
                        <path d={`M${targetX} 162v16`} {...lineStyle} />
                        <TargetShape x={targetX} kind={index === 1 ? "K" : "P"} />
                      </g>
                    ))}
                  </>
                ) : (
                  <>
                    <path d={`M${x} 142v36`} {...lineStyle} />
                    <TargetShape x={x} kind={index === 0 ? "A" : "K"} />
                  </>
                )}
                <text x={x} y={218} textAnchor="middle" fontSize={10} fill="var(--paper-ink)">
                  {t(
                    `structure.concept.${index === 0 ? (precise ? "automaticTarget" : "automatic") : precise ? (index === 1 ? "keywordOne" : "keywordTwo") : index === 1 ? "broad" : "asin"}`,
                  )}
                </text>
              </>
            )}
          </g>
        ))}
      </svg>
    </figure>
  );
}
